import { ConfigProvider, Form, Input, Modal, Select } from "antd";
import SearchSelect from "../incomingPartsTable/components/searchSelect";
import { useEffect, useState } from "react";
import {
  showToastError,
  showToastSuccess,
} from "../../../../../common/toasters/toasterMessages";
import styles from "./styles/incomingPartsModal.module.css";
import { getSuppliersAPI } from "../../../../../common/apis/commonApis";
import {
  updateIncomingPartApi,
  createIncomingPartApi,
} from "../../../apiCalls/suppliersApis";
import { FileUploader } from "../../../../../common/components/fileUploader/fileUploader";
import { ApiClient, ApiClientType } from "../../../../../clients/api-client";
import axios from "axios";

export const IncomingPartsModal = (props) => {
  const {
    showModal,
    setShowModal,
    incomingPartDetails,
    fetchIncomingPartsData,
    isEdit,
  } = props;

  const [showButton, setShowButton] = useState(false);
  const [suppliersData, setSuppliersData] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [fileList, setFileList] = useState([]);

  const SEARCH_MORE_TYPE = "searchMore";

  const [form] = Form.useForm();

  const createUpdateSupplier = async (body) => {
    let response;
    if (isEdit) {
      response = await updateIncomingPartApi(body, incomingPartDetails?.id);
      if (response.isError) {
        showToastError(response?.displayableMessage);
      } else {
        fetchIncomingPartsData();
        showToastSuccess(response?.displayableMessage);
      }
    } else {
      let response = await createIncomingPartApi(body);
      if (response.isError) {
        showToastError(response?.displayableMessage);
      } else {
        fetchIncomingPartsData();
        showToastSuccess(response?.displayableMessage);
      }
    }
  };

  const handleSave = async (values) => {
    setShowButton(false);
    let body = {
      name: values.incomingPartName,
      code: values.partCode,
      suppliers: values.suppliers,
    };
    if (values.channel !== undefined) {
      body.channel = values.channel;
    }
    await createUpdateSupplier(body);
    setShowModal(false);
    form.resetFields();
  };

  const handleSupplierSearch = async (searchVal) => {
    await fetchSuppliers(searchVal);
  };

  const fetchSuppliers = async (value, type = "") => {
    setSuppliersLoading(true);
    let suppliersResponse = await getSuppliersAPI(
      type === SEARCH_MORE_TYPE ? suppliersData.length : 0,
      value || "",
      10,
      true
    );
    if (!suppliersResponse || suppliersResponse.isError) {
      showToastError(suppliersResponse?.displayableMessage || "Failed to fetch suppliers");
    } else {
      setTotalSuppliers(suppliersResponse?.totalCount || 0);
      let dataList = type === SEARCH_MORE_TYPE ? [...suppliersData] : [];
      const list = Array.isArray(suppliersResponse?.data)
        ? suppliersResponse.data
        : [];
      list.forEach((supplier) => {
        if (supplier && supplier.id && supplier.name) {
          dataList.push({ value: supplier.id, label: supplier.name });
        }
      });
      setSuppliersData(dataList);
    }
    setSuppliersLoading(false);
  };

  const fetchMoreSuppliers = async () => {
    fetchSuppliers("", SEARCH_MORE_TYPE);
  };

  const handleCancel = () => {
    setShowModal(false);
    form.resetFields();
  };

  useEffect(() => {
    if (showModal) {
      fetchSuppliers();
    }
  }, [showModal, isEdit]);

  useEffect(() => {
    if (isEdit) {
      setShowButton(false);
      const supplierNames = incomingPartDetails?.suppliers?.map(
        (item) => item?.supplier?.id
      );
      let values = {
        incomingPartName: incomingPartDetails?.name,
        partCode: incomingPartDetails?.code,
        suppliers: supplierNames,
        channel: incomingPartDetails?.channel,
      };
      form.setFieldsValue(values);
      setFileList(
        (incomingPartDetails?.meta && incomingPartDetails?.meta?.media) ||
          incomingPartDetails?.media ||
          []
      );
    } else {
      form.resetFields();
      setShowButton(true);
      setFileList([]);
    }
  }, [isEdit, incomingPartDetails, showModal]);

  const isInUploadingState = (item) => item?.status === "uploading";

  const convertToBackendMediaObject = (newFileList) => {
    const formattedFileList = [];
    newFileList?.forEach((ele) => {
      let fileObj = {};
      if (ele.status === "done" && ele.response?.data?.url) {
        fileObj = {
          name: ele.name,
          type: ele.type,
          size: ele.size,
          url: ele.response?.data?.url,
        };
        formattedFileList.push(fileObj);
      } else if (ele.url !== "") {
        formattedFileList.push(ele);
      }
    });
    return {
      media: formattedFileList,
    };
  };

  const updateIncomingPartMedia = async (mediaObj) => {
    if (!incomingPartDetails?.id) return;
    const response = await ApiClient(
      ApiClientType.patch,
      process.env.REACT_APP_MASTER_BASE_URL,
      `/incoming-parts/${incomingPartDetails?.id}`,
      { media: mediaObj?.media }
    );
    if (!response.isError) {
      showToastSuccess(response.displayableMessage);
    } else {
      showToastError(response.displayableMessage);
    }
  };

  const onChangeFileList = (newFileList) => {
    setFileList(newFileList);
    if (newFileList?.some(isInUploadingState)) return;
    const metaObj = convertToBackendMediaObject(newFileList);
    void updateIncomingPartMedia(metaObj);
  };

  const fileUpload = async ({ file, onSuccess, onError }) => {
    const urlExtension = `/incoming-parts/${incomingPartDetails?.id}/upload`;
    const response = await ApiClient(
      ApiClientType.post,
      process.env.REACT_APP_MASTER_BASE_URL,
      urlExtension,
      {}
    );
    if (!response.isError) {
      await axios
        .put(response?.data?.uploadLink?.url, file, {
          headers: { "Content-Type": file.type },
        })
        .then(() => {
          onSuccess(response, file);
        })
        .catch((err) => {
          onError(err, file);
        });
    } else {
      showToastError("Error in uploading media");
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorTextPlaceholder: "#7d7676",
          colorBorder: "#dadada",
        },
      }}
    >
      <Modal
        title={isEdit ? "Edit Incoming Part" : "New Incoming Part"}
        open={showModal}
        onCancel={handleCancel}
        maskClosable={false}
        cancelButtonProps={{
          style: { display: "none" },
        }}
        okButtonProps={{
          style: { display: "none" },
        }}
        width={"640px"}
      >
        <Form
          onFinish={handleSave}
          form={form}
          layout="vertical"
          name="control-hooks"
        >
          <Form.Item
            className="mb-3"
            label="Incoming Part Name"
            name="incomingPartName"
            rules={[{ required: true }]}
          >
            <Input
              placeholder="Incoming Part Name"
              onFocus={() => setShowButton(true)}
            />
          </Form.Item>
          <Form.Item
            className="mb-3"
            name="partCode"
            label="Part Code"
            rules={[{ required: true }]}
          >
            <Input
              type="text"
              placeholder="Part Code"
              onWheel={(e) => e.target.blur()}
              onScroll={(e) => e.target.blur()}
              onFocus={() => setShowButton(true)}
            />
          </Form.Item>
          <Form.Item
            className="mb-3"
            name="channel"
            label="Channel"
            rules={[]}
          >
            <Select
              placeholder="Select Channel"
              onFocus={() => setShowButton(true)}
              options={[
                { value: 'red', label: 'Red' },
                { value: 'green', label: 'Green' },
                { value: 'yellow', label: 'Yellow' },
              ]}
            />
          </Form.Item>
          <Form.Item
            className="mb-3"
            name="suppliers"
            label="Suppliers"
            rules={[{ required: true }]}
          >
            <SearchSelect
              showSearch
              allowClear
              loading={suppliersLoading}
              placeholder="Select Suppliers"
              mode="multiple"
              filterOption={false}
              options={suppliersData}
              onClear={() => {
                fetchSuppliers();
              }}
              onSearch={handleSupplierSearch}
              loadMore={fetchMoreSuppliers}
              hasMoreData={totalSuppliers > suppliersData.length}
              onFocus={() => setShowButton(true)}
            />
          </Form.Item>
          {isEdit ? (
            <Form.Item className="mb-3" label="Documents">
              <FileUploader
                listType={"picture"}
                fileList={fileList}
                onChangeFileList={onChangeFileList}
                customRequest={fileUpload}
              />
            </Form.Item>
          ) : null}
          <Form.Item className={styles.btnFormItem}>
            {showButton && (
              <button type="submit" className={styles.createBtn}>
                {isEdit ? "Save changes" : "Create Incoming Part"}
              </button>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </ConfigProvider>
  );
};
